import React, { FC } from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { ReactComponent as ExpandIcon } from 'studio_react/svg/common/expand.svg';
import { ReactComponent as PauseIcon } from 'studio_react/svg/common/pause.svg';
import { ReactComponent as PlayIcon } from 'studio_react/svg/common/play.svg';
import { ReactComponent as VolumeIcon1 } from 'studio_react/svg/common/volume-1.svg';
import { ReactComponent as VolumeIcon2 } from 'studio_react/svg/common/volume-2.svg';
import { ReactComponent as VolumeIconX } from 'studio_react/svg/common/volume-x.svg';
import { ReactComponent as VolumeIcon0 } from 'studio_react/svg/common/volume.svg';
import { ReactComponent as SettingsIcon } from 'studio_react/svg/galleries/settings.svg';
import { useTranslate } from 'ts/common/hooks';
import ControlButton from './ControlButton';
import VideoSettings from './VideoSettings';

const formatTime = (s: number): string => {
    if (!isFinite(s) || isNaN(s)) {
        return '0:00';
    }

    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);

    return `${m}:${sec.toString().padStart(2, '0')}`;
};

/**
 * Diameter of the round slider thumb. The hover-reveal container around the volume slider must be
 * at least this tall: it clips with `overflow: hidden` to collapse horizontally, and a shorter box
 * crops the thumb's top and bottom, leaving a squared-off stub instead of a circle.
 */
const THUMB_SIZE = '12px';

const sliderSx = {
    appearance: 'none',
    WebkitAppearance: 'none',
    height: '4px',
    borderRadius: '2px',
    outline: 'none',
    cursor: 'pointer',
    '&::-webkit-slider-thumb': {
        WebkitAppearance: 'none',
        appearance: 'none',
        width: THUMB_SIZE,
        height: THUMB_SIZE,
        borderRadius: '50%',
        background: 'white',
        cursor: 'pointer'
    },
    '&::-moz-range-thumb': {
        width: THUMB_SIZE,
        height: THUMB_SIZE,
        borderRadius: '50%',
        background: 'white',
        cursor: 'pointer',
        border: 'none'
    }
};

interface Props {
    isPlaying: boolean;
    currentTime: number;
    duration: number;
    volume: number;
    isMuted: boolean;
    isFullscreen: boolean;
    showSettings: boolean;
    playbackSpeed: number;
    quality: string;
    qualities: string[];
    onPlayPause: () => void;
    onSeek: (time: number) => void;
    onToggleMute: () => void;
    onVolumeChange: (value: number) => void;
    onToggleFullscreen: () => void;
    onToggleSettings: () => void;
    onSpeedChange: (speed: number) => void;
    onQualityChange: (quality: string) => void;
}

const VideoControls: FC<Props> = ({
    isPlaying,
    currentTime,
    duration,
    volume,
    isMuted,
    isFullscreen,
    showSettings,
    playbackSpeed,
    quality,
    qualities,
    onPlayPause,
    onSeek,
    onToggleMute,
    onVolumeChange,
    onToggleFullscreen,
    onToggleSettings,
    onSpeedChange,
    onQualityChange
}) => {
    const t = useTranslate('studio.common.videoPlayer');
    const fillPercent = duration > 0 ? (currentTime / duration) * 100 : 0;
    const volumeFillPercent = isMuted ? 0 : volume * 100;

    const VolumeIcon =
        isMuted || volume === 0
            ? VolumeIconX
            : volume <= 0.33
            ? VolumeIcon0
            : volume <= 0.66
            ? VolumeIcon1
            : VolumeIcon2;

    return (
        <Box position="absolute" bottom={0} left={0} right={0} zIndex={2}>
            {/* Gradient for readability — behind everything */}
            <Box
                position="absolute"
                bottom={0}
                left={0}
                right={0}
                h="120px"
                background="linear-gradient(to top, rgba(0,0,0,0.75) 0%, transparent 100%)"
                pointerEvents="none"
                zIndex={0}
            />

            {/* Settings + control bar stacked in column — no absolute gap */}
            {/* stopPropagation prevents clicks here from bubbling to the video click-to-play handler */}
            <Flex
                direction="column"
                position="relative"
                zIndex={1}
                px={{ base: '8px', md: '15px' }}
                pb={{ base: '10px', md: '17px' }}
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
                {/* Settings panel — flows above control bar, right-aligned */}
                {showSettings && (
                    <Box alignSelf="flex-end" mb="8px">
                        <VideoSettings
                            playbackSpeed={playbackSpeed}
                            quality={quality}
                            qualities={qualities}
                            onSpeedChange={onSpeedChange}
                            onQualityChange={onQualityChange}
                        />
                    </Box>
                )}

                {/* Control bar */}
                <Flex
                    h={{ base: '32px', md: '40px' }}
                    alignItems="center"
                    gap={{ base: '2px', md: '4px' }}
                    minW={0}
                >
                    {/* Play / Pause — dark background */}
                    <ControlButton
                        onClick={onPlayPause}
                        ariaLabel={isPlaying ? t('pause') : t('play')}
                        bg="rgba(0,0,0,0.6)"
                        w={{ base: '36px', md: '48px' }}
                        h={{ base: '32px', md: '40px' }}
                    >
                        {isPlaying ? (
                            <PauseIcon width={20} height={20} />
                        ) : (
                            <PlayIcon width={20} height={20} />
                        )}
                    </ControlButton>

                    {/* Timeline pill */}
                    <Flex
                        flex={1}
                        minW={0}
                        bg="rgba(0,0,0,0.6)"
                        px={{ base: '6px', md: '10px' }}
                        py="4px"
                        rounded="4px"
                        gap={{ base: '4px', md: '12px' }}
                        h={{ base: '32px', md: '40px' }}
                        alignItems="center"
                    >
                        {/* Scrub bar */}
                        <Box
                            as="input"
                            type="range"
                            min={0}
                            max={duration || 1}
                            value={currentTime}
                            step={0.1}
                            aria-label={t('seek')}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                onSeek(Number(e.target.value))
                            }
                            flex={1}
                            minW={0}
                            style={{
                                background: `linear-gradient(to right, white ${fillPercent}%, rgba(255,255,255,0.3) ${fillPercent}%)`
                            }}
                            sx={sliderSx}
                        />

                        {/* Time */}
                        <Text color="white" fontSize="14px" whiteSpace="nowrap" flexShrink={0}>
                            {formatTime(currentTime)} / {formatTime(duration)}
                        </Text>

                        {/* Volume — icon + slider revealed on hover */}
                        <Flex alignItems="center" gap="6px" role="group">
                            <ControlButton
                                onClick={onToggleMute}
                                ariaLabel={isMuted ? t('unmute') : t('mute')}
                            >
                                <VolumeIcon width={24} height={24} />
                            </ControlButton>

                            {/* `_focusWithin` matters as much as `_groupHover`: the slider stays
                                in the tab order while collapsed, so without it a keyboard user
                                lands on a control clipped to zero width. */}
                            <Box
                                w="0px"
                                h={THUMB_SIZE}
                                display="flex"
                                alignItems="center"
                                overflow="hidden"
                                transition="width 0.2s ease"
                                flexShrink={0}
                                _groupHover={{ w: '72px' }}
                                _focusWithin={{ w: '72px' }}
                            >
                                <Box
                                    as="input"
                                    type="range"
                                    min={0}
                                    max={1}
                                    step={0.02}
                                    value={isMuted ? 0 : volume}
                                    aria-label={t('volume')}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                        onVolumeChange(Number(e.target.value))
                                    }
                                    w="72px"
                                    display="block"
                                    style={{
                                        background: `linear-gradient(to right, white ${volumeFillPercent}%, rgba(255,255,255,0.3) ${volumeFillPercent}%)`
                                    }}
                                    sx={sliderSx}
                                />
                            </Box>
                        </Flex>

                        {/* Settings */}
                        <ControlButton onClick={onToggleSettings} ariaLabel={t('settings')}>
                            <SettingsIcon width={24} height={24} />
                        </ControlButton>

                        {/* Fullscreen — Figma expand icon */}
                        <ControlButton
                            onClick={onToggleFullscreen}
                            ariaLabel={isFullscreen ? t('exitFullscreen') : t('fullscreen')}
                        >
                            <Box
                                w="24px"
                                h="24px"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                            >
                                <ExpandIcon width={20} height={20} />
                            </Box>
                        </ControlButton>
                    </Flex>
                </Flex>
            </Flex>
        </Box>
    );
};

export default VideoControls;
